import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';

const TIME_OPTIONS = [
  '6:00 AM', '6:30 AM', '7:00 AM', '7:30 AM', '8:00 AM', '8:30 AM',
  '9:00 AM', '9:30 AM', '10:00 AM', '10:30 AM', '11:00 AM', '11:30 AM',
  '12:00 PM', '12:30 PM', '1:00 PM', '1:30 PM', '2:00 PM', '2:30 PM',
  '3:00 PM', '3:30 PM', '4:00 PM', '4:30 PM', '5:00 PM', '5:30 PM',
  '6:00 PM', '6:30 PM', '7:00 PM', '7:30 PM', '8:00 PM'
];

const AvailabilityCalendar = ({ availability, ownerPickupTime, ownerDropoffTime, onDateSelect }) => {
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [selectedStartDate, setSelectedStartDate] = useState(null);
  const [selectedEndDate, setSelectedEndDate] = useState(null);
  const [selectedPickupTime, setSelectedPickupTime] = useState('');
  const [selectedDropoffTime, setSelectedDropoffTime] = useState('');

  const monthNames = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'
  ];

  const getDaysInMonth = (date) => {
    const year = date?.getFullYear();
    const month = date?.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay?.getDate();
    const startingDayOfWeek = firstDay?.getDay();
    return { daysInMonth, startingDayOfWeek };
  };

  const isDateAvailable = (date) => {
    const dateString = date?.toISOString()?.split('T')?.[0];
    return availability?.includes(dateString);
  };

  const isDateInRange = (date) => {
    if (!selectedStartDate || !selectedEndDate) return false;
    return date >= selectedStartDate && date <= selectedEndDate;
  };

  const handleDateClick = (date) => {
    if (!isDateAvailable(date)) return;
    if (!selectedStartDate || (selectedStartDate && selectedEndDate)) {
      setSelectedStartDate(date);
      setSelectedEndDate(null);
    } else {
      if (date < selectedStartDate) {
        setSelectedStartDate(date);
        setSelectedEndDate(null);
      } else {
        setSelectedEndDate(date);
        if (onDateSelect) {
          onDateSelect(selectedStartDate, date, selectedPickupTime, selectedDropoffTime);
        }
      }
    }
  };

  const handlePickupTimeChange = (time) => {
    setSelectedPickupTime(time);
    if (selectedStartDate && selectedEndDate && onDateSelect) {
      onDateSelect(selectedStartDate, selectedEndDate, time, selectedDropoffTime);
    }
  };

  const handleDropoffTimeChange = (time) => {
    setSelectedDropoffTime(time);
    if (selectedStartDate && selectedEndDate && onDateSelect) {
      onDateSelect(selectedStartDate, selectedEndDate, selectedPickupTime, time);
    }
  };

  const handlePreviousMonth = () => {
    setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1));
  };

  const handleNextMonth = () => {
    setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1));
  };

  const { daysInMonth, startingDayOfWeek } = getDaysInMonth(currentMonth);
  const days = [];
  for (let i = 0; i < startingDayOfWeek; i++) { days?.push(null); }
  for (let day = 1; day <= daysInMonth; day++) {
    days?.push(new Date(currentMonth.getFullYear(), currentMonth.getMonth(), day));
  }

  // Filter time options based on owner constraints
  const getPickupTimeOptions = () => {
    if (!ownerPickupTime) return TIME_OPTIONS;
    const ownerIdx = TIME_OPTIONS?.indexOf(ownerPickupTime);
    return ownerIdx >= 0 ? TIME_OPTIONS?.slice(ownerIdx) : TIME_OPTIONS;
  };

  const getDropoffTimeOptions = () => {
    if (!ownerDropoffTime) return TIME_OPTIONS;
    const ownerIdx = TIME_OPTIONS?.indexOf(ownerDropoffTime);
    return ownerIdx >= 0 ? TIME_OPTIONS?.slice(0, ownerIdx + 1) : TIME_OPTIONS;
  };

  return (
    <div className="bg-card rounded-lg shadow-elevation-2 p-4 md:p-6 lg:p-8">
      <h2 className="font-heading text-2xl md:text-3xl font-semibold text-foreground mb-6">
        Availability Calendar
      </h2>
      {/* Owner time window info */}
      {(ownerPickupTime || ownerDropoffTime) && (
        <div className="mb-6 p-3 bg-primary/5 border border-primary/20 rounded-md flex items-start space-x-2">
          <Icon name="Clock" size={16} color="var(--color-primary)" strokeWidth={2} className="mt-0.5 flex-shrink-0" />
          <p className="font-body text-sm text-foreground">
            <span className="font-semibold">Owner's hours:</span>{' '}
            {ownerPickupTime && <>Pick-up from <strong>{ownerPickupTime}</strong></>}
            {ownerPickupTime && ownerDropoffTime && ' · '}
            {ownerDropoffTime && <>Drop-off by <strong>{ownerDropoffTime}</strong></>}
          </p>
        </div>
      )}
      <div className="flex items-center justify-between mb-6">
        <button onClick={handlePreviousMonth} className="w-10 h-10 flex items-center justify-center rounded-md hover:bg-muted transition-smooth" aria-label="Previous month">
          <Icon name="ChevronLeft" size={20} strokeWidth={2} />
        </button>
        <h3 className="font-heading text-lg md:text-xl font-semibold text-foreground">
          {monthNames?.[currentMonth?.getMonth()]} {currentMonth?.getFullYear()}
        </h3>
        <button onClick={handleNextMonth} className="w-10 h-10 flex items-center justify-center rounded-md hover:bg-muted transition-smooth" aria-label="Next month">
          <Icon name="ChevronRight" size={20} strokeWidth={2} />
        </button>
      </div>
      <div className="grid grid-cols-7 gap-1 md:gap-2 mb-2">
        {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat']?.map((day) => (
          <div key={day} className="text-center font-caption text-xs md:text-sm font-medium text-muted-foreground py-2">{day}</div>
        ))}
      </div>
      <div className="grid grid-cols-7 gap-1 md:gap-2">
        {days?.map((date, index) => {
          if (!date) return <div key={index} className="aspect-square" />;
          const isAvailable = isDateAvailable(date);
          const isInRange = isDateInRange(date);
          const isStart = selectedStartDate && date?.getTime() === selectedStartDate?.getTime();
          const isEnd = selectedEndDate && date?.getTime() === selectedEndDate?.getTime();
          const isPast = date < new Date(new Date().setHours(0, 0, 0, 0));
          return (
            <button
              key={index}
              onClick={() => handleDateClick(date)}
              disabled={!isAvailable || isPast}
              className={`aspect-square flex items-center justify-center rounded-md font-body text-sm md:text-base transition-smooth ${
                isPast ? 'text-muted-foreground/30 cursor-not-allowed'
                  : !isAvailable ? 'text-muted-foreground/50 cursor-not-allowed' : isStart || isEnd ?'bg-primary text-primary-foreground font-semibold' : isInRange ?'bg-primary/20 text-foreground' :'text-foreground hover:bg-muted'
              }`}
            >
              {date?.getDate()}
            </button>
          );
        })}
      </div>
      <div className="mt-6 pt-6 border-t border-border space-y-4">
        <div className="flex flex-wrap gap-4 text-sm">
          <div className="flex items-center space-x-2">
            <div className="w-4 h-4 bg-primary rounded" />
            <span className="font-body text-muted-foreground">Selected</span>
          </div>
          <div className="flex items-center space-x-2">
            <div className="w-4 h-4 bg-primary/20 rounded" />
            <span className="font-body text-muted-foreground">In Range</span>
          </div>
          <div className="flex items-center space-x-2">
            <div className="w-4 h-4 bg-muted-foreground/20 rounded" />
            <span className="font-body text-muted-foreground">Unavailable</span>
          </div>
        </div>

        {selectedStartDate && selectedEndDate && (
          <div className="p-4 bg-muted rounded-md space-y-4">
            <p className="font-body text-sm md:text-base text-foreground">
              <span className="font-semibold">Selected Dates:</span>{' '}
              {selectedStartDate?.toLocaleDateString()} - {selectedEndDate?.toLocaleDateString()}
            </p>

            {/* Time selection for renter */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 pt-2 border-t border-border">
              <div>
                <label className="block font-caption text-xs font-medium text-muted-foreground uppercase mb-1.5">
                  Pick-up Time
                </label>
                <div className="relative">
                  <select
                    value={selectedPickupTime}
                    onChange={(e) => handlePickupTimeChange(e?.target?.value)}
                    className="w-full appearance-none bg-background border border-border rounded-md px-3 py-2 pr-8 font-body text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-primary/50 focus:border-primary transition-smooth"
                  >
                    <option value="">Select time</option>
                    {getPickupTimeOptions()?.map((time) => (
                      <option key={time} value={time}>{time}</option>
                    ))}
                  </select>
                  <Icon name="ChevronDown" size={14} className="absolute right-2.5 top-1/2 -translate-y-1/2 text-muted-foreground pointer-events-none" strokeWidth={2} />
                </div>
              </div>
              <div>
                <label className="block font-caption text-xs font-medium text-muted-foreground uppercase mb-1.5">
                  Drop-off Time
                </label>
                <div className="relative">
                  <select
                    value={selectedDropoffTime}
                    onChange={(e) => handleDropoffTimeChange(e?.target?.value)}
                    className="w-full appearance-none bg-background border border-border rounded-md px-3 py-2 pr-8 font-body text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-primary/50 focus:border-primary transition-smooth"
                  >
                    <option value="">Select time</option>
                    {getDropoffTimeOptions()?.map((time) => (
                      <option key={time} value={time}>{time}</option>
                    ))}
                  </select>
                  <Icon name="ChevronDown" size={14} className="absolute right-2.5 top-1/2 -translate-y-1/2 text-muted-foreground pointer-events-none" strokeWidth={2} />
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AvailabilityCalendar;